// Stewardship Financial's AUM fee schedule — the ONE implementation.
//
// Grant's statement of the fee (2026-09-01, correcting the earlier
// either/or reading in comp-tables-v0.2):
//
//   The tiered AUM fee applies to ALL managed assets:
//       1.25% of the first $500k
//       1.00% of the next $1.0M   (500k → 1.5M)
//       0.75% of the next $3.5M   (1.5M → 5.0M)
//       0.50% of everything above $5.0M
//   AND, when AUM is under $200k, a $189/month financial planning fee
//   is charged IN ADDITION ("there's ALSO a $189/month financial
//   planning fee") — billed through AdvicePay, not deducted by the
//   custodian.
//
// [History: until 2026-09-01 this function treated the $189/month as a
// REPLACEMENT for the tiered fee below $200k, per the doc's
// "mutually exclusive" rule. Production billing data disproved that —
// Betterment charges the tiered rate on every managed balance, however
// small — and Grant confirmed the additive reading, so revenue
// estimates for small-AUM households were understated by the tiered
// portion.]
//
// It lived in shared/db/dashboard-jake.ts (server-only — that module
// pulls in the D1 client). Extracted here as a pure function so the web
// bundle can use it too — the Data Needed "Processing numbers" editor
// auto-fills a Financial record's Year-1 revenue from the entered AUM.
// dashboard-jake.ts re-exports this under its original name
// `estimatedAnnualRevenueCents` so its existing callers + tests are
// unchanged.
//
// Pure (no DB / no React imports).

/** AUM below which the additive $189/month planning fee applies, in
 *  whole dollars. Exported so the dashboard's "N households under $200K
 *  paying the planning fee" line uses the same number this function
 *  charges against, rather than a second hardcoded literal. */
export const PLANNING_FEE_AUM_THRESHOLD_DOLLARS = 200_000;

/** Same threshold in whole cents, for callers that already work in
 *  cents (per-household AUM sums are cents throughout the dashboard
 *  layer) — avoids every caller re-deriving `* 100`. */
export const PLANNING_FEE_AUM_THRESHOLD_CENTS =
  PLANNING_FEE_AUM_THRESHOLD_DOLLARS * 100;

/**
 * Estimated annual revenue to Stewardship Financial from a household's
 * AUM, in whole cents: the tiered AUM fee on the full balance, plus the
 * $189/month planning fee when AUM is under $200k.
 */
export function estimatedFinancialAnnualRevenueCents(aumCents: number): number {
  const aum = Math.max(0, aumCents) / 100; // dollars
  let revenue = 0;
  const tier1 = Math.min(aum, 500_000);
  revenue += tier1 * 0.0125;
  if (aum > 500_000) {
    const tier2 = Math.min(aum - 500_000, 1_000_000);
    revenue += tier2 * 0.01;
  }
  if (aum > 1_500_000) {
    const tier3 = Math.min(aum - 1_500_000, 3_500_000);
    revenue += tier3 * 0.0075;
  }
  if (aum > 5_000_000) {
    revenue += (aum - 5_000_000) * 0.005;
  }
  if (aum < PLANNING_FEE_AUM_THRESHOLD_DOLLARS) {
    revenue += 189 * 12; // additive planning fee, per Grant 2026-09-01
  }
  return Math.round(revenue * 100);
}
